import { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Typography,
  IconButton,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/FolderOpen';
import { useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  useListProjectsQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
} from '../redux/firestoreApi';
import { setActiveProject } from '../redux/sessionSlice';
import { showToast } from '../redux/uiSlice';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import ConfirmDialog from '../components/common/ConfirmDialog';
import type { Project } from '../types/models';

interface FormData {
  projectName: string;
  projectCode: string;
  description?: string;
}

export default function ProjectsPage() {
  const uid = useAppSelector((s) => s.session.uid)!;
  const activeId = useAppSelector((s) => s.session.activeProjectId);
  const dispatch = useAppDispatch();

  const { data: projects = [], isLoading } = useListProjectsQuery({ uid });
  const [create] = useCreateProjectMutation();
  const [update] = useUpdateProjectMutation();
  const [remove] = useDeleteProjectMutation();

  const [editing, setEditing] = useState<Project | null>(null);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Project | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } =
    useForm<FormData>();

  const openCreate = () => {
    setEditing(null);
    reset({ projectName: '', projectCode: '', description: '' });
    setOpen(true);
  };

  const openEdit = (p: Project) => {
    setEditing(p);
    reset({
      projectName: p.projectName,
      projectCode: p.projectCode,
      description: p.description ?? '',
    });
    setOpen(true);
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (editing) {
        await update({ uid, id: editing.id, patch: data }).unwrap();
        dispatch(showToast({ message: 'Project updated', severity: 'success' }));
      } else {
        const created = await create({ uid, ...data }).unwrap();
        dispatch(setActiveProject(created.id));
        dispatch(showToast({ message: 'Project created', severity: 'success' }));
      }
      setOpen(false);
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Save failed', severity: 'error' }));
    }
  });

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await remove({ uid, id: toDelete.id }).unwrap();
      dispatch(showToast({ message: 'Project deleted', severity: 'success' }));
      if (activeId === toDelete.id) dispatch(setActiveProject(null));
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Delete failed', severity: 'error' }));
    }
  };

  return (
    <>
      <PageHeader
        title="Projects"
        subtitle="Top-level workspaces. Sprints and tasks live inside a project."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New project
          </Button>
        }
      />

      {!isLoading && projects.length === 0 ? (
        <EmptyState
          icon={<FolderIcon fontSize="inherit" />}
          title="No projects yet"
          description="Create your first project to start managing sprints."
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Create project
            </Button>
          }
        />
      ) : (
        <Grid container spacing={2}>
          {projects.map((p) => (
            <Grid item xs={12} sm={6} md={4} key={p.id}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderColor: activeId === p.id ? 'primary.main' : undefined,
                  borderWidth: activeId === p.id ? 2 : 1,
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Typography variant="h6">{p.projectName}</Typography>
                    <Chip
                      size="small"
                      label={p.projectCode}
                      color="primary"
                      variant="outlined"
                    />
                  </Stack>
                  {p.description && (
                    <Typography
                      color="text.secondary"
                      variant="body2"
                      sx={{ mt: 1 }}
                    >
                      {p.description}
                    </Typography>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button
                    size="small"
                    onClick={() => dispatch(setActiveProject(p.id))}
                    disabled={activeId === p.id}
                  >
                    {activeId === p.id ? 'Active' : 'Set active'}
                  </Button>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton size="small" onClick={() => openEdit(p)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setToDelete(p)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit project' : 'New project'}</DialogTitle>
        <form onSubmit={onSubmit}>
          <DialogContent>
            <Stack spacing={2}>
              <TextField
                label="Project name"
                autoFocus
                error={!!errors.projectName}
                helperText={errors.projectName?.message}
                {...register('projectName', { required: 'Required' })}
              />
              <TextField
                label="Project code"
                placeholder="FRT"
                error={!!errors.projectCode}
                helperText={errors.projectCode?.message}
                {...register('projectCode', { required: 'Required' })}
              />
              <TextField
                label="Description"
                multiline
                rows={3}
                {...register('description')}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editing ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        title="Delete project?"
        message={`"${toDelete?.projectName}" and all its sprints/tasks references will be removed. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}
